<script lang="ts">
  import { orgArrowLeft, orgArrowRight } from "@nrk/origo";
  import type { Image } from "bildebanken-model";
  import { getBildebankenHost } from "config";
  import { isSearchHit, type SearchHit } from "services/searchHit";
  import {
    onlineState,
    getCloseDetailsLink,
    pageState,
    preloadNeighbors,
    showDetailsForNextItem,
    showDetailsForPreviousItem,
    type ImageSource,
    type navigationDirection,
  } from "state/page";
  import {
    createMetadataQuery,
    getMimirSearchHitIds,
    getMimirSearchHits,
  } from "state/queries/mimir";
  import { onDestroy, onMount } from "svelte";
  import { fade } from "svelte/transition";
  import { getErrorMessage } from "utils/fns";
  import ImagePreview from "components/Common/ImagePreview.svelte";
  import FullscreenImage from "./FullscreenImage.svelte";
  import Metadata from "./Metadata.svelte";
  import { getNtbSearchHits } from "../../state/queries/ntb";
  import { nrkChevronLeft } from "@nrk/core-icons";
  import AddToCollectionDialog from "../Collections/AddToCollectionDialog.svelte";
  import RelatedImages from "./RelatedImages.svelte";
  import { detailAltText } from "state/detailAltText";

  export let itemId: string;
  export let itemSource: ImageSource = "Bildebanken";
  let image: Image | undefined = undefined;
  let lastBoundImageId = "";

  let fullscreen = false;

  function toggleFullscreen() {
    fullscreen = !fullscreen;
  }

  let searchHits;
  $: if (itemSource === "Bildebanken") {
    searchHits = getMimirSearchHits();
  } else if (itemSource === "Ntb") {
    searchHits = getNtbSearchHits();
  }
  $: searchHitIds = getMimirSearchHitIds();

  $: searchHit = $searchHits.find((item) => item.id === itemId);

  $: query = createMetadataQuery(itemId, itemSource);
  $: image = $query.isSuccess ? $query.data : undefined;

  // preload Image while loading metadata
  const preload = new Image();
  preload.src = getPreviewUrl(searchHit);

  let direction: navigationDirection = "forwards";
  // Sync alt text from metadata only when navigating to a different image.
  $: if (image?.id && image.id !== lastBoundImageId) {
    lastBoundImageId = image.id;
    detailAltText.set(image.metadata?.altText || "");
  }
  // Preload proxy for neighboring search hits, for faster loading when navigating with arrows
  $: $searchHits.length > 0 &&
    itemSource === "Bildebanken" &&
    preloadNeighbors(itemId, $searchHits, { direction, amount: 5, itemSource: itemSource });

  onMount(() => {
    document.addEventListener("keydown", arrowNavigate);
  });

  onDestroy(() => {
    document.removeEventListener("keydown", arrowNavigate);
  });

  function getPreviewUrl(image?: Image | SearchHit) {
    // high-res is too large and can't be cached because the URL is different each time (because of signing)
    // proxy is also way too large (because it's PNG), but smaller than highRes on average
    return (
      (isSearchHit(image) ? image.previewUrl : image?.proxy) ??
      new URL("/image-icon.svg", getBildebankenHost()).href
    );
  }

  // To avoid two-way binding on variable 'fullscreen'
  function setIsFullscreen(value: boolean) {
    fullscreen = value;
  }

  function arrowNavigate(event: KeyboardEvent & { target: Element }) {
    if (event.metaKey || event.altKey || event.ctrlKey) return;
    if (event.code !== "ArrowRight" && event.code !== "ArrowLeft") return;
    if (event.target.nodeName === "INPUT" || event.target.nodeName === "TEXTAREA") return;

    if (event.code === "ArrowRight") {
      navigateToNextImage();
    } else {
      navigateToPreviousImage();
    }
  }

  const isNoAccessError = (error: unknown) => {
    return (
      error &&
      typeof error === "object" &&
      "message" in error &&
      error.message === "No access to this page"
    );
  };

  const externalPatterns = [/\/images\/ntb\//];
  $: externalImage = externalPatterns.some((re) => re.test(window.location.pathname));

  function navigateToPreviousImage() {
    if (itemSource === "Ntb") return showDetailsForPreviousItem($searchHits.map((hit) => hit.id));
    else return showDetailsForPreviousItem($searchHitIds);
  }

  function navigateToNextImage() {
    if (itemSource === "Ntb") return showDetailsForNextItem($searchHits.map((hit) => hit.id));
    else return showDetailsForNextItem($searchHitIds);
  }
</script>

<div class="detail-container">
  {#if $onlineState === "OFFLINE"}
    <div class="status" in:fade={{ duration: 200, delay: 300 }}>
      Kan ikke hente detaljer for bildet da Bildebanken ikke får kontakt med Internett.
    </div>
  {/if}
  {#if $query.isLoading && $onlineState === "ONLINE"}
    <div class="status" in:fade={{ duration: 200, delay: 300 }}>Laster metadata for ...</div>
  {:else if $query.isError}
    {#if isNoAccessError($query.error)}
      <div class="state-wrapper">
        <h3>Ingen tilgang</h3>
        <p>Bildet har begrenset synlighet.</p>
      </div>
    {:else}
      <div class="status">
        <p>Kunne ikke laste bildet med id <code>{itemId}</code>. Ugyldig lenke?</p>
        <div class="org-warning">{getErrorMessage($query.error)}</div>
      </div>
    {/if}
  {:else}
    <div class="left" class:fullscreen>
      {#if !fullscreen && image}
        <div>
          <a href={getCloseDetailsLink($pageState)} class="org-button">
            {@html nrkChevronLeft} Tilbake
          </a>
        </div>
        <div class="image-wrapper">
          <!-- Use cached search hit if available,
                 as signed AWS URL to proxy can be
                 different in searchHit and image. -->
          <button
            aria-label="Forrige bilde"
            class="org-button navigation navigation-prev"
            on:click={navigateToPreviousImage}>{@html orgArrowLeft}</button
          >
          <div class="preview-container">
            <ImagePreview
              mode="detail"
              toggleFullScreen={toggleFullscreen}
              image={image || searchHit}
              {itemSource}
            />
          </div>
          <button
            aria-label="Neste bilde"
            class="org-button navigation navigation-next"
            on:click={navigateToNextImage}>{@html orgArrowRight}</button
          >
        </div>

        <RelatedImages
          jobId={image?.metadata?.jobId}
          {itemId}
          {itemSource}
          {searchHits}
          hasRelatedImage={image.hasRelations}
        />
      {/if}
      <AddToCollectionDialog selectedImages={[itemId]} isNtb={itemSource === "Ntb"} />

      {#if image}
        <!-- Rename imageType? f.eks: mediaType -->
        <FullscreenImage
          src={getPreviewUrl(image)}
          srcHighRes={image.highRes ?? image.proxy}
          imageType={image.mediaType.split("/")?.pop()?.toUpperCase()}
          isFullscreen={fullscreen}
          {setIsFullscreen}
        />
      {/if}
    </div>

    <div class="right">
      <h2 class="metadata-header">Om bildet</h2>

      {#if image}
        <Metadata
          {image}
          {externalImage}
          {itemSource}
          {itemId}
          altText={$detailAltText}
          onAltTextChange={(text) => detailAltText.set(text)}
        />
      {/if}
    </div>
  {/if}
</div>

<style>
  .detail-container {
    width: 100%;
    max-width: 100%;
    position: relative;
    display: flex;
    height: 100%;
    min-height: 0;
  }

  .left {
    /* hack to not force right side out of screen when we have related images */
    min-width: 0;

    padding: var(--org-medium);
    flex: 1;
    display: flex;
    flex-basis: 75%;
    flex-direction: column;
  }

  .right {
    flex: 1;
    min-width: 500px;
    padding: var(--org-medium);
    background: var(--color-surface-secondary);
    overflow-y: scroll;
  }

  @media (max-width: 768px) {
    .detail-container {
      flex-direction: column;
      overflow: auto;
    }

    .left {
      flex-basis: auto;
    }

    .left:not(.fullscreen) {
      max-height: 90vh;
    }

    .right {
      overflow-y: visible;
      min-width: 0;
      width: 100%;
    }
  }

  .navigation {
    border-radius: var(--org-large);
    position: absolute;
    top: 50%;
    transform: translateY(-50%);
    background-color: var(--org-color-shade-2);
  }

  .navigation:hover {
    background-color: var(--org-color-shade-3);
  }

  .navigation-prev {
    left: var(--org-small);
  }
  .navigation-next {
    right: var(--org-small);
  }

  .image-wrapper {
    /* hack to keep portrait images inside screen */
    min-height: 0;

    display: flex;
    align-items: center;
    justify-content: center;
    flex: 1;
    width: 100%;
    background-color: var(--color-surface-tertiary);
    position: relative;
  }

  .preview-container {
    width: 100%;
    height: 100%;
    display: flex;
    justify-content: center;
  }
  .status {
    padding: var(--org-medium);
  }
  .fullscreen {
    display: flex;
    flex-direction: column;
    justify-content: center;
    position: fixed;
    top: 0;
    bottom: 0;
    left: 0;
    right: 0;
    padding: 20px;
    z-index: 3;
    object-fit: contain;
  }

  .metadata-header {
    margin-top: calc(-1 * var(--org-small));
  }

  .state-wrapper {
    display: flex;
    flex-direction: column;
    justify-content: center;
    align-items: center;
    color: #888;
    width: 100%;
  }
</style>
